import { PlatformLocation } from '@angular/common';
import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { KnownRequestInstrumentationEvents } from '@office-start/telemetry-types';
import { take } from 'rxjs/operators';

import { AnnouncementType } from '../../banner/announcement-type.enum';
import { AnnouncementModel } from '../../banner/announcement.model';
import { OfficeRoute } from '../constants/office-route.enum';
import { InstrumentationParam, InstrumentationService, KnownInstrumentationEvents } from '../instrumentation/instrumentation.service';
import { FetchStrategy, InstrumentedHttp } from '../instrumentation/instrumented-http.service';
import { FormsCreateLocation, FormsCreateLocationType } from '../models/create-location.model';
import { ErrorDialogErrorCategory } from '../models/error-dialog.model';
import { HttpResponseContentType } from '../models/http-response-content-type.enum';
import { KnownWorkloadReferralNames } from '../models/known-workload-referral-names.model';
import { TabItemAttribution } from '../models/tab-item.model';
import { OfficeDotComOrigin, OfficeDotComPrvOrigin } from '../models/telemetry-constants.model';
import { toFileItemFromForm } from '../mru/mru-data-converter.utility';
import { MruDataService } from '../mru/mru-data.service';
import { MruFileFormat, MruFileItem } from '../mru/mru-file-item.model';
import { AngularEvents } from '../shared-context/angular-events';
import { ContextService } from '../shared-context/context.service';
import { Template } from '../templates/templates.model';
import { TemplatesService } from '../templates/templates.service';
import { DataStructureUtility } from '../utilities/data-structure.utility';
import { GuidGenerator } from '../utilities/guid-generator.utility';
import { UrlParser } from '../utilities/url-parser.utility';
import { InstrumentedHttpDataResponse, InstrumentedHttpPromise } from './../instrumentation/instrumented-http.service';
import { FileType } from './../models/file-item.model';
import { Form, FormsGroup, FormsGroupMember, FormsHelperFunctions, FormsResponseCount, FormsThemeMappingObject, FormsThemeMappingSet, FormType, GetFormResponse, GetFormsResponse, GetResponseCountsResponse, UserReputationTier } from './../models/form.model';
import { OperationStream } from './../models/operation-stream.model';
import { AuthType, ClientStartupConfigService } from './../shared-context/client-startup-config.service';
import { LocService } from './../shared-context/loc.service';
import { HashUtility } from './../utilities/hash.utility';
import { ObservableHelper } from './../utilities/observable-helper.utility';
import { UrlBuilder } from './../utilities/url-builder.utility';
import { AccessTokenConsumer, AccessTokenService } from './access-token.service';
import { DeviceDetector } from './device-detector.service';
import { EventService } from './event.service';
import { FormsConfigService } from './forms-config.service';
import { DESIGNER_MOBILE_URL, DESIGNER_URL, NEO_DESIGN_URL } from './forms.constants';
import { OfficeRouteItem, OfficeRouteService } from './office-route.service';
import { OfficeHomeApiConfigService } from './officehome-api-config.service';
import { SchedulerService } from './scheduler.service';
import { UrlAuthenticatorService } from './url-authenticator.service';
import { XhrService } from './xhr.service';

const customizedThemeName = 'Customized Theme';

/**
 * Service that makes requests to Forms apis.
*/
@Injectable({
    providedIn: 'root'
})
export class FormsService extends XhrService {
    private static readonly BASE_URL: string = '/formapi/api';
    private static readonly SHARED_FORMS_PATH = `${FormsService.BASE_URL}/me/sharedWithMeForms`;
    private static readonly MY_FORMS_PATH = `${FormsService.BASE_URL}/me/light/forms?$select=id,title,modifiedDate,settings,ownerId,ownerTenantId,category,background,otherInfo,softDeleted,status,type&$filter=type+ne+%27survey%27`;
    private static readonly FORMS_RESPONSE_COUNT_PATH = `${FormsService.BASE_URL}/light/GetRespCounts()`;
    private static readonly FORMS_THUMBNAIL_MOCK_PATH = '/content/mockData/shared/services/mockData/form/thumbnails';
    private static readonly FORMS_THEMES_MOCK = '/Themes';
    private static readonly FORMS_THEMES = '/Themes.json';
    private static readonly MY_FORMS_GROUPS = `${FormsService.BASE_URL}/groups`;
    private static readonly USER_REPUTATION_TIER_PATH = `${FormsService.BASE_URL}/userReputationTier`;
    // tslint:disable: member-ordering
    private static readonly GET_FORM = (tenantId: string, ownerType: FormType, ownerId: string, formId: string) => `${FormsService.BASE_URL}/${tenantId}/${ownerType}/${ownerId}/light/forms('${formId}')?$select=id,rowCount,background,otherInfo,settings,type,category`;
    private static readonly COPY_FORMS_PATH = (tenantId: string, formType: FormType, userId: string, formId: string) => `${FormsService.BASE_URL}/${tenantId}/${formType}/${userId}/light/forms('${formId}')/CopyToUser`;
    private static readonly MOVE_FORM_PATH = (tenantId: string, formType: FormType, userId: string, formId: string) => `${FormsService.BASE_URL}/${tenantId}/${formType}/${userId}/light/forms('${formId}')/MoveForm`;
    private static readonly FORMS_MEMBER_THUMBNAIL_PATH = (groupId: string, members: string) => `${FormsService.BASE_URL}/groups('${groupId}')/members('${members}')/photo/$value`;
    private static readonly FORMS_GROUP_MEMBERS = (groupId: string) => `${FormsService.BASE_URL}/groups('${groupId}')/members`;
    private static readonly FORM_EDIT_OR_DELETE_PATH = (tenantId: string, formType: FormType, userId: string, formId: string) => `${FormsService.BASE_URL}/${tenantId}/${formType}/${userId}/forms('${formId}')`;
    private static readonly FORMS_FROM_GROUP = (tenantId: string, groupId: string) => `${FormsService.BASE_URL}/${tenantId}/groups/${groupId}/light/forms?$select=id,title,modifiedDate,ownerId,ownerTenantId,softDeleted,category,rowCount,background,otherInfo,type&$filter=type+ne+%27survey%27`;
    private static readonly MY_FORMS_GROUP_THUMBNAIL_PATH = (groupId: string) => `${FormsService.BASE_URL}/groups('${groupId}')/photo/$value`;
    private static readonly SHARED_FORM = (formsId: string) => `${FormsService.BASE_URL}/light/sharedWithMeForms('${formsId}')`;
    private static readonly CREATE_FORM_FROM_TEMPLATE = (tenantId: string, ownerType: FormType, ownerId: string) => `${FormsService.BASE_URL}/${tenantId}/${ownerType}/${ownerId}/light/forms`;

    private getFormsGroupsDataStreamCache: OperationStream<GetFormsResponse<FormsGroup>> = null;
    private getMyFormsDataStreamCache: OperationStream<GetFormsResponse<Form>> = null;
    private getSharedFormsDataStreamCache: OperationStream<GetFormsResponse<Form>> = null;

    private getMyFormsResponsesCache: Promise<Array<FormsResponseCount>> = null;
    private getFormsThemeMappingSetCache: Promise<FormsThemeMappingSet> = null;
    private readonly getFormDataPromisesCache: { [key: string]: Promise<Form> } = {};

    private readonly getFormsGroupMembersStreamCache: { [key: string]: OperationStream<GetFormsResponse<FormsGroupMember>> } = {};
    private readonly getFormsInGroupDataStreamCache: { [key: string]: OperationStream<GetFormsResponse<Form>> } = {};
    private readonly tenantId: string;
    private readonly guid: string;

    private getReputationTierCache: Promise<UserReputationTier>;

    constructor(
        accessTokenService: AccessTokenService,
        public eventService: EventService,
        instrumentationService: InstrumentationService,
        instrumentedHttp: InstrumentedHttp,
        contextService: ContextService,
        private readonly locService: LocService,
        officeHomeApiConfigService: OfficeHomeApiConfigService,
        public readonly formsConfigService: FormsConfigService,
        public readonly officeRouteService: OfficeRouteService,
        platformLocation: PlatformLocation,
        scheduler: SchedulerService,
        private readonly startupConfigService: ClientStartupConfigService,
        private readonly clientStartupConfigService: ClientStartupConfigService,
        private readonly urlAuthenticatorService: UrlAuthenticatorService,
        public readonly deviceDetector: DeviceDetector,
        private readonly templatesService: TemplatesService,
        private readonly mruDataService: MruDataService
    ) {
        super(
            accessTokenService,
            instrumentationService,
            instrumentedHttp,
            AccessTokenConsumer.Forms,
            contextService,
            officeHomeApiConfigService,
            platformLocation,
            scheduler
        );
        const clientStartupConfig = this.clientStartupConfigService.config;
        if (this.startupConfigService.config.authType === AuthType.MSA) {
            this.tenantId = this.formsConfigService.config.formsMsaTenantId;
            this.guid = this.convertConsumerPuidToGuid(clientStartupConfig.puid);
        } else {
            this.tenantId = clientStartupConfig.tenantId;
            this.guid = clientStartupConfig.objectId;
        }
    }

    public onInitFormsStartPage() {
        // For MSA, we will need the popup dialog if the user's browser is blocking third-party cookies (Safari, Chrome incognito).
        // for OrgId, a popup dialog will be needed if the token fetch fails due to a CAP error.
        this.accessTokenService.getAccessToken(AccessTokenConsumer.Forms, false, null, true, { category: ErrorDialogErrorCategory.CreateFile, fileType: FileType.Forms });
        this.setFormsCompromisedUserAlert();
    }

    public getFormsTermsOfServiceUrl(): string {
        return this.formsConfigService.config.termsOfServiceLinkOrgId;
    }

    public getForm(formId: string, ownerType: FormType, ownerId: string, refreshCache?: boolean): Promise<Form> {
        if (!refreshCache && this.getFormDataPromisesCache[formId]) {
            return this.getFormDataPromisesCache[formId];
        }
        this.getFormDataPromisesCache[formId] = new Promise<Form>((resolve, reject) => {
            this.get<GetFormResponse<Form>>(KnownInstrumentationEvents.GetForm, KnownRequestInstrumentationEvents.GetForm, this.formsConfigService.config.formsApiPath + FormsService.GET_FORM(this.tenantId, ownerType, ownerId, formId), (promise: InstrumentedHttpPromise<any>) => {
                promise.instrument((result, params) => {
                    params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                }).then(response => {
                    if (response && response.data && response.data.id) {
                        const url = this.getFormUrl(response.data.id);
                        response.data = { ...response.data, fromCache: response.fromCache };
                        response.data.url = url;
                        response.data.ownerPrincipalType = ownerType;
                        if (response.data.otherInfo) {
                            response.data.otherInfo = JSON.parse(response.data.otherInfo as string);
                        }
                        resolve(response.data);
                    }
                });
            },
                reject,
                this.getRequestConfig(),
                FetchStrategy.NetworkFirst
            );
        });

        return this.getFormDataPromisesCache[formId];
    }

    public unpinSharedForm(formId: string): Promise<void> {
        return new Promise((resolve, reject) => {
            this.delete(KnownInstrumentationEvents.FormsSharedUnpinRequest,
                KnownRequestInstrumentationEvents.FormsSharedUnpinRequest,
                this.formsConfigService.config.formsApiPath + FormsService.SHARED_FORM(formId),
                null,
                (promise: InstrumentedHttpPromise<any>) => {
                    promise.instrument((result, params) => {
                        params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                    }).then(res => {
                        resolve(res.data);
                    });
                },
                error => {
                    reject(error);
                },
                this.getRequestConfig());
        });
    }

    /**
     * Copies a form to the targeted owner type (user or group)
     * @param id form identifier
     * @param oldOwnerId id of the original form owner
     * @param newOwnerId id of the new form owner
     * @param newFormType owner type (user or group)
     */
    public postCopyForm(id: string, title: string, oldTenantId: string, oldOwnerId: string, newOwnerId: string, oldFormType: FormType, newFormType: FormType): Promise<Form> {
        const isNewOwnerGroup = newFormType === FormType.Groups;
        return new Promise((resolve, reject) => {
            const payload = {
                newOwnerId,
                newTenantId: this.tenantId,
                isNewOwnerGroup,
                patchProperties: { title }
            };
            const successCallback = (promise: InstrumentedHttpPromise<any>) => {
                promise.instrument((result, params) => {
                    params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                }).then(res => {
                    const otherInfo = res.data.otherInfo;
                    const formId = res.data.id;
                    const url = this.getFormUrl(formId);
                    const form: Form = {
                        ...res.data,
                        otherInfo: JSON.parse(otherInfo),
                        url
                    };

                    // Clear cache for where the form is copied to since the number of forms increased.
                    this.clearCache(newFormType, newOwnerId);
                    formsSubscription.unsubscribe();
                    resolve(form);
                });
            };
            const formsSubscription = (isNewOwnerGroup ? this.getFormsInGroup(newOwnerId) : this.getMyForms())
                .results.subscribe(formsResponse => {
                    if (formsResponse && formsResponse.fromCache) {
                        return;
                    }
                    if (formsResponse && formsResponse.value) {
                        const titleList = formsResponse.value.filter(item => !item.softDeleted).map(item => item.title);
                        payload.patchProperties.title = getUniqueNameFromList(title, titleList);
                    }
                    this.post(KnownInstrumentationEvents.FormsCopyRequest,
                        KnownRequestInstrumentationEvents.FormsCopyRequest,
                        this.formsConfigService.config.formsApiPath + FormsService.COPY_FORMS_PATH(oldTenantId, oldFormType, oldOwnerId, id),
                        payload,
                        successCallback,
                        error => {
                            reject(error);
                        },
                        this.getRequestConfig());
                });
        });
    }

    public createForm(formType: FileType.Forms | FileType.FormsQuiz, overrideLocationSelection?: FormsCreateLocation, clickLocation?: string): Promise<string> {
        return new Promise(resolve => {
            // Tries to use override otherwise, user selected location, otherwise default to myForms
            const saveLocation: FormsCreateLocation = overrideLocationSelection ? overrideLocationSelection : { type: FormsCreateLocationType.USERS };
            resolve(this.getCreateFormsLink(formType, saveLocation.identifier, clickLocation));
        });
    }

    public createFormFromTemplate(template: Template, formType: FormType, ownerId?: string): Promise<string> {
        const targetOwnerId: string = ownerId || this.guid;
        return new Promise((resolve, reject) => {
            this.templatesService.getTemplateContent(FileType.Forms, template.AssetId, template.TemplateBinaryUrl).results.pipe(take(1)).subscribe(res => {
                this.post<Form>(
                    KnownInstrumentationEvents.FormCreateFormFromTemplate,
                    KnownRequestInstrumentationEvents.FormCreateFormFromTemplate,
                    this.formsConfigService.config.formsApiPath +
                    FormsService.CREATE_FORM_FROM_TEMPLATE(this.tenantId, formType, targetOwnerId),
                    res.data.Content.Value,
                    (promise: InstrumentedHttpPromise<Form>) => {
                        promise.instrument((result, params) => {
                            params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                            params[InstrumentationParam.ElementId] = template.AssetId;
                        }).then(response => {
                            resolve(this.getCreateFormFromTemplateLink(response.data.id));
                        });
                    },
                    error => {
                        reject(error);
                    },
                    this.getRequestConfig(),
                    null,
                    null,
                    null,
                    null,
                    undefined,
                    null,
                    null,
                    null,
                    true,
                    { category: ErrorDialogErrorCategory.CreateFile, fileType: FileType.Forms });
            });
        });
    }

    public postMoveForms(formId: string, formType: FormType, ownerId: string, targetFormType: FormType, targetGroupId: string) {
        return new Promise<Form>((resolve, reject) => {
            this.post<any>(
                KnownInstrumentationEvents.RecycleFormRequest,
                KnownRequestInstrumentationEvents.RecycleFormRequest,
                this.formsConfigService.config.formsApiPath +
                FormsService.MOVE_FORM_PATH(this.tenantId, formType, ownerId, formId),
                {
                    isNewOwnerGroup: targetFormType === FormType.Groups,
                    newOwnerId: targetGroupId
                },
                (promise: InstrumentedHttpPromise<Form>) => {
                    promise.instrument((result, params) => {
                        params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);

                        return result;
                    }).then(result => {
                        // Reloads the source and the target since a move changes two locations.
                        this.clearCache(formType, ownerId);
                        this.clearCache(targetFormType, targetGroupId);
                        resolve(result.data);
                    });
                },
                error => {
                    reject(error);
                },
                this.getRequestConfig());
        });
    }

    public getFormAuthenticatedCdn(isNeoPage: boolean) {
        const clientContext = this.clientStartupConfigService.config.sharedClientStartupContext;
        const isScuProdPrv = clientContext.deploymentEnvironment === 'prodprv' && clientContext.geoName === 'scu';
        let authenticatedUrl = this.urlAuthenticatorService.appendPvrAndUpnAuthParams(
            this.formsConfigService.config.formsCdnPath +
            (
                isNeoPage ?
                    NEO_DESIGN_URL :
                    (this.deviceDetector.isMobileDevice() || this.deviceDetector.isTabletDevice() ? DESIGNER_MOBILE_URL : DESIGNER_URL)
            )
        );
        authenticatedUrl = UrlBuilder.appendAndReplaceQueryParams(authenticatedUrl, {
            origin: isScuProdPrv ? OfficeDotComPrvOrigin : OfficeDotComOrigin,
            lang: this.contextService.sharedContext.officeMarket,
            sessionId: this.startupConfigService.config.sharedClientStartupContext.sessionId
        }, false, true);
        return authenticatedUrl;
    }

    public getFormGroupThumbnailUrl(groupId: string): Promise<string> {
        return this.getImage(this.formsConfigService.config.formsApiPath + FormsService.MY_FORMS_GROUP_THUMBNAIL_PATH(groupId), KnownInstrumentationEvents.FormsGroupThumbnailFetch, KnownRequestInstrumentationEvents.FormsGroupThumbnailFetch);
    }

    public getFormsInGroup(groupId: string, refreshCache?: boolean): OperationStream<GetFormsResponse<Form>> {
        if (refreshCache) {
            this.getFormsInGroupDataStreamCache[groupId] = null;
        }
        if (this.getFormsInGroupDataStreamCache[groupId]) {
            return this.getFormsInGroupDataStreamCache[groupId];
        }
        const fetchGetFormsInGroup = (fetchStrategy: FetchStrategy) => new Promise<GetFormsResponse<Form>>((resolve, reject) => {
            this.get<GetFormsResponse<Form>>(KnownInstrumentationEvents.FormsFromGroupRequest, KnownRequestInstrumentationEvents.FormsFromGroupRequest, this.formsConfigService.config.formsApiPath + FormsService.FORMS_FROM_GROUP(this.tenantId, groupId), (promise: InstrumentedHttpPromise<any>) => {
                promise.instrument((result, params) => {
                    params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                }).then(response => {
                    if (response && response.data && response.data.value) {
                        response.data = { ...response.data, fromCache: response.fromCache };
                        response.data.value = DataStructureUtility.objectToArrayHelper(response.data.value);
                        response.data.value.forEach((form: Form) => {
                            if (form.otherInfo) {
                                form.otherInfo = JSON.parse(form.otherInfo as string);
                            }
                            form.ownerPrincipalType = FormType.Groups;
                            form.url = this.getFormUrl(form.id);
                        });
                        resolve(response.data);
                    }
                });
            }, error => {
                reject(error);
            }, this.getRequestConfig(),
                fetchStrategy);
        });

        const getFetchGetFormsGroupsFromCache = fetchGetFormsInGroup(FetchStrategy.CacheOnly);
        const getFetchGetFormsGroupsFromNetwork = fetchGetFormsInGroup(FetchStrategy.NetworkFirst);
        const getFormsInGroupDataStream = ObservableHelper.createResultsWithCacheObservable<GetFormsResponse<Form>>(
            getFetchGetFormsGroupsFromCache,
            getFetchGetFormsGroupsFromNetwork,
            null,
            _ => {
                this.getFormsInGroupDataStreamCache[groupId] = null;
            }
        );
        this.getFormsInGroupDataStreamCache[groupId] = getFormsInGroupDataStream;
        return this.getFormsInGroupDataStreamCache[groupId];
    }

    public getFormsGroupMembers(groupId: string): OperationStream<GetFormsResponse<FormsGroupMember>> {
        if (this.getFormsGroupMembersStreamCache[groupId]) {
            return this.getFormsGroupMembersStreamCache[groupId];
        }

        const fetchGetFormsGroupMembers = (fetchStrategy: FetchStrategy) => new Promise<GetFormsResponse<FormsGroupMember>>((resolve, reject) => {
            this.get<GetFormsResponse<FormsGroup>>(KnownInstrumentationEvents.FormsGroupMembersRequest, KnownRequestInstrumentationEvents.FormsGroupMembersRequest, this.formsConfigService.config.formsApiPath + FormsService.FORMS_GROUP_MEMBERS(groupId), (promise: InstrumentedHttpPromise<any>) => {
                promise.instrument((result, params) => {
                    params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                }).then(response => {
                    resolve(response.data);
                });
            }, error => {
                reject(error);
            }, this.getRequestConfig(),
                fetchStrategy);
        });

        const getFetchGetFormsGroupsFromCache = fetchGetFormsGroupMembers(FetchStrategy.CacheOnly);
        const getFetchGetFormsGroupsFromNetwork = fetchGetFormsGroupMembers(FetchStrategy.NetworkFirst);
        const getFormsGroupMembers = ObservableHelper.createResultsWithCacheObservable<GetFormsResponse<FormsGroupMember>>(
            getFetchGetFormsGroupsFromCache,
            getFetchGetFormsGroupsFromNetwork,
            null,
            _ => this.getFormsGroupMembersStreamCache[groupId] = null
        );

        this.getFormsGroupMembersStreamCache[groupId] = getFormsGroupMembers;
        return getFormsGroupMembers;
    }

    public getFormsGroups(): OperationStream<GetFormsResponse<FormsGroup>> {
        if (this.getFormsGroupsDataStreamCache) {
            return this.getFormsGroupsDataStreamCache;
        }

        const fetchGetFormsGroups = (fetchStrategy: FetchStrategy) => new Promise<GetFormsResponse<FormsGroup>>((resolve, reject) => {
            this.get<GetFormsResponse<FormsGroup>>(
                KnownInstrumentationEvents.FormsGroupsRequest,
                KnownRequestInstrumentationEvents.FormsGroupsRequest,
                this.formsConfigService.config.formsApiPath + FormsService.MY_FORMS_GROUPS,
                (promise: InstrumentedHttpPromise<any>) => {
                    promise.instrument((result, params) => {
                        params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                    }).then(response => {
                        this.mruDataService.getMruDocuments(new Set([FileType.Forms])).results.subscribe(mruData => {
                            const groupIdsMap = new Map<string, number>();
                            mruData.items.forEach((item, index) => {
                                if (item.Extension === 'GroupForm' && groupIdsMap[item.MetaData.FormsMetaData.owner_id] === undefined) {
                                    groupIdsMap[item.MetaData.FormsMetaData.owner_id] = index;
                                }
                            });
                            const sorter = (a: FormsGroup, b: FormsGroup) => {
                                if (groupIdsMap[a.id] >= 0 && groupIdsMap[b.id] >= 0) {
                                    return groupIdsMap[a.id] - groupIdsMap[b.id];
                                } else if (groupIdsMap[a.id] >= 0) {
                                    return -1;
                                } else {
                                    return 1;
                                }
                            };
                            response.data.value.sort(sorter);
                            resolve(response.data);
                        });
                        this.mruDataService.getMruDocuments(new Set([FileType.Forms])).initialCompletion.then(error => {
                            if (error && error.cacheError && error.networkError) {
                                resolve(response.data);
                            }
                        });
                    });
                }, error => {
                    reject(error);
                }, this.getRequestConfig(),
                fetchStrategy);
        });

        const getFetchGetFormsGroupsFromCache = fetchGetFormsGroups(FetchStrategy.CacheOnly);
        const getFetchGetFormsGroupsFromNetwork = fetchGetFormsGroups(FetchStrategy.NetworkFirst);
        const getFormsGroupsDataStream = ObservableHelper.createResultsWithCacheObservable<GetFormsResponse<FormsGroup>>(
            getFetchGetFormsGroupsFromCache,
            getFetchGetFormsGroupsFromNetwork,
            null,
            _ => this.getFormsGroupsDataStreamCache = null
        );

        this.getFormsGroupsDataStreamCache = getFormsGroupsDataStream;
        return getFormsGroupsDataStream;
    }

    /**
     * Performs a restore or a recycle on a form.
     * @param formId
     * @param ownerId
     * @param formType
     * @param isRecycle
     */
    public setFormsRecycleState(formId: string, ownerId: string, formType: FormType, isRecycle: boolean): Promise<void> {
        const knownInstrumentationEvent: KnownInstrumentationEvents = isRecycle ? KnownInstrumentationEvents.RecycleFormRequest : KnownInstrumentationEvents.RestoreFormRequest;
        const otelKnownInstrumentationEvent: KnownRequestInstrumentationEvents = isRecycle ? KnownRequestInstrumentationEvents.RecycleFormRequest : KnownRequestInstrumentationEvents.RestoreFormRequest;
        return new Promise<void>((resolve, reject) => {
            this.patch<any>(
                knownInstrumentationEvent,
                otelKnownInstrumentationEvent,
                this.formsConfigService.config.formsApiPath +
                FormsService.FORM_EDIT_OR_DELETE_PATH(this.tenantId, formType, ownerId, formId),
                {
                    softDeleted: isRecycle ? 1 : 0
                },
                (promise: InstrumentedHttpPromise<any>) => {
                    promise.instrument((result, params) => {
                        params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                    }).then(() => {
                        this.clearCache(formType, ownerId);
                        resolve();
                    });
                },
                error => {
                    reject(error);
                },
                this.getRequestConfig());
        });
    }

    /**
     * Performs a hard delete on a form.
     * @param formId
     * @param ownerId
     * @param formType
     */
    public hardDeleteForm(formId: string, ownerId: string, formType: FormType): Promise<any> {
        return new Promise<void>((resolve, reject) => {
            this.delete(
                KnownInstrumentationEvents.FormsDeleteRequest,
                KnownRequestInstrumentationEvents.FormsDeleteRequest,
                this.formsConfigService.config.formsApiPath +
                FormsService.FORM_EDIT_OR_DELETE_PATH(this.tenantId, formType, ownerId, formId),
                {},
                (promise: InstrumentedHttpPromise<any>) => {
                    promise.instrument((result, params) => {
                        params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                    }).then(() => {
                        // Hard deleted forms cause the number of forms in the group to decrease thus we need to refresh.
                        this.clearCache(formType, ownerId);
                        resolve();
                    });
                },
                error => {
                    reject(error);
                });
        });
    }

    public getCreateFormsLink(fileType: FileType, groupId?: string, clickLocation?: string): string {
        const actions = {
            CreateSurvey: 'Create',
            CreateQuiz: 'CreateQuiz'
        };
        const fragmentTemplate = (title: string, action: string, grpId?: string) => `Action=${action}&Title=${title}${grpId ? '&GroupId=' + grpId : ''}`;
        let url = this.getFormAuthenticatedCdn(false);
        const params: Params = {
            Action: null,
            Fragment: null
        };
        if (fileType === FileType.Forms) {
            params.Fragment = fragmentTemplate(this.locService.NewFormsSurveyFileName, actions.CreateSurvey, groupId);
            params.Action = actions.CreateSurvey;
        } else if (fileType === FileType.FormsQuiz) {
            // Currently, for Mobile Quiz Creation, Forms not support MobileDesignPage.
            url = url.replace(DESIGNER_MOBILE_URL, DESIGNER_URL);
            params.Fragment = fragmentTemplate(this.locService.NewFormsQuizFileName, actions.CreateQuiz, groupId);
            params.Action = actions.CreateQuiz;
        } else {
            // Only Forms File Types are supported.
            return null;
        }
        url = UrlBuilder.appendAndReplaceQueryParams(url, params, true);
        return this.appendQueryInformation(url, true, clickLocation);
    }

    public getFormUrl(formId: string): string {
        return `${this.getFormAuthenticatedCdn(false)}#FormId=${formId}`;
    }

    public getAllMyFormsLink(): TabItemAttribution {
        const allMyFormsLink = {
            title: this.locService.AllMyFormsRedirectLabel,
            tooltip: this.locService.AllMyFormsRedirectTooltip,
            ariaLabel: this.locService.AllMyFormsRedirectTooltip,
            referral: KnownWorkloadReferralNames.Forms,
            zeroTitle: this.locService.AllMyFormsRedirectLabel
        };

        if (this.formsConfigService.config.neoPortalPageEnabled) {
            return {
                ...allMyFormsLink,
                url: this.urlAuthenticatorService.appendPvrAndUpnAuthParams(this.getFormAuthenticatedCdn(true))
            };
        } else {
            return {
                ...allMyFormsLink,
                onClick: () => {
                    this.officeRouteService.navigateTo({
                        app: FileType.Forms,
                        route: OfficeRoute.MyForms
                    });
                }
            };
        }
    }

    public getCreateFormFromTemplateLink(formId: string): string {
        let url = this.getFormAuthenticatedCdn(false);
        const params: Params = {
            Action: 'CreateByTemplate',
            Fragment: `FormId=${formId}`
        };
        url = UrlBuilder.appendAndReplaceQueryParams(url, params, true);

        return this.appendQueryInformation(url, true);
    }

    public getFormGroupMemberThumbnailUrl(groupId: string, memberId: string): Promise<string> {
        return this.getImage(this.formsConfigService.config.formsApiPath + FormsService.FORMS_MEMBER_THUMBNAIL_PATH(groupId, memberId), KnownInstrumentationEvents.FormsMembersThumbnailFetch, KnownRequestInstrumentationEvents.FormsMembersThumbnailFetch);
    }

    public getMyFormsResponses(): Promise<Array<FormsResponseCount>> {
        if (!this.getMyFormsResponsesCache) {
            this.getMyFormsResponsesCache = new Promise<Array<FormsResponseCount>>((resolve, reject) => {
                const url = this.formsConfigService.config.formsApiPath + FormsService.FORMS_RESPONSE_COUNT_PATH;
                this.get<GetResponseCountsResponse>(
                    KnownInstrumentationEvents.FormsResponses,
                    KnownRequestInstrumentationEvents.FormsResponses,
                    url,
                    (promise: InstrumentedHttpPromise<GetResponseCountsResponse>) => {
                        promise.instrument((result, params) => {
                            params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                        }).then(response => {
                            if (response && response.data) {
                                resolve(DataStructureUtility.objectToArrayHelper<FormsResponseCount>(response.data.value));
                            } else {
                                reject();
                            }
                        });
                    },
                    reject,
                    this.getRequestConfig(),
                    FetchStrategy.NetworkFirst
                );
            });
        }

        return this.getMyFormsResponsesCache;
    }

    public getSharedForms(): OperationStream<GetFormsResponse<Form>> {
        if (this.getSharedFormsDataStreamCache) {
            return this.getSharedFormsDataStreamCache;
        }
        const fetchGetSharedForms = (fetchStrategy: FetchStrategy) => new Promise<GetFormsResponse<Form>>((resolve, reject) => {
            this.get<GetFormsResponse<Form>>(KnownInstrumentationEvents.SharedFormsRequest, KnownRequestInstrumentationEvents.SharedFormsRequest, this.formsConfigService.config.formsApiPath + FormsService.SHARED_FORMS_PATH, (promise: InstrumentedHttpPromise<any>) => {
                promise.instrument((result, params) => {
                    params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                }).then(response => {
                    if (response && response.data && response.data.value) {
                        response.data.value.sort((a: Form, b: Form) => {
                            if (a.modifiedDate === b.modifiedDate) {
                                return 0;
                            } else if (a.modifiedDate < b.modifiedDate) {
                                return 1;
                            } else {
                                return -1;
                            }
                        });
                        response.data.value.forEach((form: Form) => {
                            if (form.id) {
                                form.url = this.getFormUrl(form.id);
                                // Forms fields are inconsistent. They are fixing this so now we have to resort to the lowercase one but as the service rolls out, some data might still have the capitalized form.
                                form.ownerPrincipalType = FormsHelperFunctions.getOwnerPrincipalTypeToFormType(form.ownerType || form.ownerPrincipalType || form.OwnerPrincipalType);
                            }
                        });
                        resolve(response.data);
                    } else {
                        reject();
                    }
                });
            }, error => {
                reject(error);
            }, this.getRequestConfig(),
                fetchStrategy);
        });

        const getSharedFormsFromCache = fetchGetSharedForms(FetchStrategy.CacheOnly);
        const getSharedFormsFromNetwork = fetchGetSharedForms(FetchStrategy.NetworkFirst);
        const getSharedFormsDataStream = ObservableHelper.createResultsWithCacheObservable(
            getSharedFormsFromCache,
            getSharedFormsFromNetwork,
            null,
            _ => this.getSharedFormsDataStreamCache = null
        );
        this.getSharedFormsDataStreamCache = getSharedFormsDataStream;
        return getSharedFormsDataStream;
    }

    public setFormsCompromisedUserAlert(): void {
        if (!this.getReputationTierCache) {
            this.getReputationTierCache = this.getUserReputationTier();
        }
        this.getReputationTierCache.then(userTier => {
            if (userTier === UserReputationTier.Bad) {
                this.pushFormsAnnouncements(AnnouncementType.FormsCompromisedUser);
            }
        });
    }

    /**
     * Broadcasts the announcement and remembers the last announcement that was sent to the user,
     * so even though the user dismisses it on this session,it will not get shown again.
     * Another announcement will only be shown if there is a different announcement to be shown.
     * @param announcementType The announcement type that need to be push.
     * @param formName Form name of the action.
     */
    public pushFormsAnnouncements(announcementType: AnnouncementType, formName?: string): void {
        const errorCssName = 'announcement-background-color-wrapper--error';
        const alertCssName = 'announcement-background-color-wrapper--alert';
        const fontCssClass = 'announcement-section__title--black announcement-close__button--black';

        switch (announcementType) {
            case AnnouncementType.FormsCompromisedUser:
                this.eventService.broadcastEvent({
                    name: AngularEvents.newAnnouncement,
                    args: [
                        new AnnouncementModel(
                            announcementType,
                            this.locService.FormsCompromisedUserAlertMessage,
                            null,
                            '',
                            '',
                            false,
                            errorCssName,
                            fontCssClass,
                            null,
                            null,
                            {
                                text: this.locService.TermsOfUseLink,
                                url: this.getFormsTermsOfServiceUrl(),
                                shouldFocusOnInit: false
                            }
                        )
                    ]
                });
                break;
            case AnnouncementType.FormsMoveToRecycleBin:
                const message = this.locService.FormsMoveToRecycleBinMessageNew;
                if (message) {
                    this.eventService.broadcastEvent({
                        name: AngularEvents.newAnnouncement,
                        args: [
                            new AnnouncementModel(
                                announcementType,
                                message,
                                null,
                                '',
                                '',
                                false,
                                alertCssName,
                                fontCssClass,
                                null,
                                null,
                                null,
                                5
                            )
                        ]
                    });
                }
                break;
            case AnnouncementType.FormsRestored:
                this.eventService.broadcastEvent({
                    name: AngularEvents.newAnnouncement,
                    args: [
                        new AnnouncementModel(
                            announcementType,
                            this.locService.format(this.locService.FormsRestoredMessage, `"${formName}"`),
                            null,
                            '',
                            '',
                            false,
                            alertCssName,
                            fontCssClass
                        )
                    ]
                });
                break;
            case AnnouncementType.FormsPermanentlyDeleted:
                this.eventService.broadcastEvent({
                    name: AngularEvents.newAnnouncement,
                    args: [
                        new AnnouncementModel(
                            announcementType,
                            this.locService.format(this.locService.FormsPermanentlyDeletedMessage, `"${formName}"`),
                            null,
                            '',
                            '',
                            false,
                            alertCssName,
                            fontCssClass
                        )
                    ]
                });
                break;
            default:
                break;
        }
    }

    public setFormsCopySuccessAnnouncement(formName: string, formType: FormType, showAllFormsHint: boolean, newOwnerId?: string, newOwnerName?: string) {
        let url: string, locString: string, titleText: string;
        switch (formType) {
            case FormType.Users:
                locString = this.locService.FormsCopyToMyFormsMessage;
                titleText = this.locService.AllMyFormsRedirectLabel;
                url = this.officeRouteService.getUrlForRoute({
                    app: FileType.Forms,
                    route: OfficeRoute.MyForms
                });
                break;
            case FormType.Groups:
                locString = this.locService.FormsCopyToGroupMessage;
                titleText = newOwnerName;
                url = this.officeRouteService.getUrlForRoute({
                    app: FileType.Forms,
                    route: OfficeRoute.GroupForms
                }, { groupId: newOwnerId });
                break;
            default:
                break;
        }

        this.eventService.broadcastEvent({
            name: AngularEvents.newAnnouncement,
            args: [
                new AnnouncementModel(
                    AnnouncementType.FormsCopied,
                    this.locService.format(locString, `"${formName}"`),
                    null,
                    '',
                    '',
                    false,
                    'announcement-background-color-wrapper--alert',
                    'announcement-section__title--black announcement-close__button--black',
                    null,
                    null,
                    showAllFormsHint ? {
                        url,
                        text: titleText,
                        shouldFocusOnInit: true
                    } : null
                )
            ]
        });
    }

    public setFormsMoveToGroupSuccessAnnouncement(formName: string, groupId: string, groupName: string) {
        const urlForGroupForms = this.officeRouteService.getUrlForRoute({
            app: FileType.Forms,
            route: OfficeRoute.GroupForms
        }, { groupId });

        this.eventService.broadcastEvent({
            name: AngularEvents.newAnnouncement,
            args: [
                new AnnouncementModel(
                    AnnouncementType.FormsMoveToGroup,
                    this.locService.format(this.locService.FormsMoveToGroupMessage, `"${formName}"`),
                    null,
                    '',
                    '',
                    false,
                    'announcement-background-color-wrapper--alert',
                    'announcement-section__title--black announcement-close__button--black',
                    null,
                    null,
                    {
                        url: urlForGroupForms,
                        text: groupName,
                        shouldFocusOnInit: true
                    }
                )
            ]
        });
    }

    public getMyForms(refreshCache?: boolean): OperationStream<GetFormsResponse<Form>> {
        if (refreshCache) {
            this.getMyFormsDataStreamCache = null;
        }
        if (this.getMyFormsDataStreamCache) {
            return this.getMyFormsDataStreamCache;
        }
        const fetchGetMyForms = (fetchStrategy: FetchStrategy) => new Promise<GetFormsResponse<Form>>((resolve, reject) => {
            this.get<GetFormsResponse<Form>>(KnownInstrumentationEvents.MyFormsRequest, KnownRequestInstrumentationEvents.MyFormsRequest, this.formsConfigService.config.formsApiPath + FormsService.MY_FORMS_PATH, (promise: InstrumentedHttpPromise<any>) => {
                promise.instrument((result, params) => {
                    params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                }).then(response => {
                    response.data = { ...response.data, fromCache: response.fromCache };
                    // Todo: not sure if on a single item if an array of 1 is returned or simply the object is returned. Assuming array for now.
                    if (response && response.data && response.data.value) {
                        response.data.value = DataStructureUtility.objectToArrayHelper(response.data.value);
                        response.data.value.forEach((form: Form) => {
                            form.ownerPrincipalType = FormType.Users;
                            if (form.otherInfo) {
                                form.otherInfo = JSON.parse(form.otherInfo as string);
                            }
                            form.url = this.getFormUrl(form.id);
                        });
                        response.data.value = response.data.value.sort((form1: Form, form2: Form) => new Date(form2.modifiedDate).getTime() - new Date(form1.modifiedDate).getTime());
                    }
                    resolve(response.data);
                });
            }, error => {
                reject(error);
            }, this.getRequestConfig(),
                fetchStrategy);
        });

        const getMyFormsFromCache = fetchGetMyForms(FetchStrategy.CacheOnly);
        const getMyFormsFromNetwork = fetchGetMyForms(FetchStrategy.NetworkFirst);
        const getMyFormsDataStream = ObservableHelper.createResultsWithCacheObservable<GetFormsResponse<Form>>(
            getMyFormsFromCache,
            getMyFormsFromNetwork,
            null,
            _ => {
                this.getMyFormsDataStreamCache = null;
            }
        );
        this.getMyFormsDataStreamCache = getMyFormsDataStream;
        return this.getMyFormsDataStreamCache;
    }

    public fetchFormThumbnail(item: MruFileItem): Promise<void> {
        const getPath = () => this.inMockMode ? `${location.origin}${FormsService.FORMS_THUMBNAIL_MOCK_PATH}` : `${this.formsConfigService.config.formsApiPath}`;
        const getDefaultImages = (basePath: string) => ({
            defaultImage: `${basePath}/Images/Theme/${this.getFormsDefaultImageName(item.fileName)}`,
            defaultColor: this.formsConfigService.config.defaultThumbnailColor
        });
        const setThumbnailColor = (fillColor: string) => {
            item.thumbnails.backgroundColor = fillColor;
        };
        const setThumbnailUrl = (url: string) => {
            item.thumbnails.small = url;
            item.thumbnails.medium = url;
            item.thumbnails.large = url;
            item.thumbnails.recommendedThumbnail = url;
        };
        const setupThumbnail = (): Promise<void> => new Promise(resolve => {
            const basePath = getPath();
            const defaultImageAndBackground = getDefaultImages(basePath);
            let imageUrlChoice = item.metaData && item.metaData.background_image ? (this.inMockMode ? `${basePath}${UrlParser.getLocation(item.metaData.background_image).path}` : item.metaData.background_image) : defaultImageAndBackground.defaultImage;
            let backgroundColor = item.metaData && item.metaData.primary_color ? item.metaData.primary_color : defaultImageAndBackground.defaultColor;

            // Recent meta data already contains absolute link to the thumbnail and does not need to obtain the mapping.
            if (item.format === MruFileFormat.Recent) {
                setThumbnailColor(backgroundColor);
                setThumbnailUrl(imageUrlChoice);
                resolve();
                return;
            }

            if (item.thumbnailInfo && item.thumbnailInfo.thumbnailFillColor) {
                backgroundColor = item.thumbnailInfo.thumbnailFillColor;
            }

            if (item.thumbnailInfo?.thumbnailId === customizedThemeName && item.thumbnailInfo?.thumbnailUrl) {
                imageUrlChoice = item.thumbnailInfo.thumbnailUrl;
                setThumbnailColor(backgroundColor);
                setThumbnailUrl(imageUrlChoice);
                resolve();
            } else if (item.thumbnailInfo && item.thumbnailInfo.thumbnailId) {
                this.getFormsThemingData().then((mappingSet: FormsThemeMappingSet) => {
                    const nameToThemeMapping: FormsThemeMappingObject = mappingSet[item.thumbnailInfo.thumbnailId];
                    if (nameToThemeMapping) {
                        if (nameToThemeMapping.SecondaryColor) {
                            backgroundColor = nameToThemeMapping.SecondaryColor;
                        }
                        if (nameToThemeMapping.BackgroundImage) {
                            imageUrlChoice = this.inMockMode ? `${location.origin}${FormsService.FORMS_THUMBNAIL_MOCK_PATH}${nameToThemeMapping.BackgroundImage}` :
                                `${this.formsConfigService.config.formsApiPath}${nameToThemeMapping.BackgroundImage}`;
                        }
                    }
                    setThumbnailColor(backgroundColor);
                    setThumbnailUrl(imageUrlChoice);
                    resolve();
                }).catch(() => {
                    setThumbnailColor(backgroundColor);
                    setThumbnailUrl(imageUrlChoice);
                    resolve();
                });
            } else {
                setThumbnailColor(backgroundColor);
                setThumbnailUrl(imageUrlChoice);
                resolve();
            }
        });
        if (item.format === MruFileFormat.SharedForm) {
            return new Promise(resolve => {
                this.getForm(item.serverDocId, item.ownerType, item.ownerId)
                    .then(res => {
                        const thumbnailPopulatedData = toFileItemFromForm(
                            res,
                            null,
                            null,
                            null,
                            this.clientStartupConfigService.config.authType,
                            res.fromCache
                        );
                        item.thumbnailInfo = thumbnailPopulatedData.thumbnailInfo;
                        setupThumbnail().then(resolve).catch(resolve);
                    })
                    .catch(() => {
                        const defaultImageAndBackground = getDefaultImages(getPath());
                        setThumbnailColor(defaultImageAndBackground.defaultColor);
                        setThumbnailUrl(defaultImageAndBackground.defaultImage);
                        resolve();
                    });
            });
        } else {
            return setupThumbnail();
        }
    }

    protected handleQueryParam(paramNameInLowerCase: string, value: string | Array<string>) {
        if (paramNameInLowerCase === 'mockformsservicemode' && this.mockRequestHeader) {
            this.getRequestConfig().headers.set(this.mockRequestHeader, value.toString());
        }
    }

    protected getRequestIdFromResponse(response: InstrumentedHttpDataResponse<any>): string {
        return this.getHeaderValue(response.headers, 'x-correlationid');
    }

    private getImage(url: string, knownInstrumentationEvent: KnownInstrumentationEvents, otelKnownInstrumentationEvent: KnownRequestInstrumentationEvents): Promise<string> {
        return new Promise((resolve, reject) => {
            this.get<GetFormsResponse<Form>>(knownInstrumentationEvent, otelKnownInstrumentationEvent, url, (promise: InstrumentedHttpPromise<any>) => {
                promise.instrument((result, params) => {
                    params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                }).then(response => {
                    if (response && response.data) {
                        const reader = new FileReader();
                        reader.addEventListener('load', () => {
                            // Some old photo uploaded by IE has filetype image/pjpeg instead of jpeg.
                            // Hack this to let generated base64 string use jpeg.
                            let result = reader.result.toString();
                            result = result.replace('data:image/pjpeg', 'data:image/jpeg');
                            resolve(result);
                        }, false);
                        reader.readAsDataURL(response.data);
                    }
                });
            }, error => {
                reject(error);
            }, {
                responseType: HttpResponseContentType.Blob
            }, FetchStrategy.NetworkOnly);
        });
    }

    private appendQueryInformation(url: string, isEncoded?: boolean, clickLocation?: string) {
        const officeRouteItem: OfficeRouteItem = this.officeRouteService.getCurrentRoute();
        const params: Params = {
            route: clickLocation || (officeRouteItem && officeRouteItem.route ? officeRouteItem.route : undefined)
        };
        return UrlBuilder.appendAndReplaceQueryParams(url, params, isEncoded);
    }

    private getRequestConfig() {
        return {
            headers: new Map([
                ['Accept', 'application/json'],
                ['x-ms-form-request-source', 'ms-startpage'],
                ['x-usersessionid', this.startupConfigService.config.sharedClientStartupContext.sessionId],
                ['x-correlationid', GuidGenerator.createGuid()],
                ['x-anchormailbox', `${this.guid}@${this.tenantId}`]
            ])
        };
    }

    /**
     * Clears the cache data.
     * @param formType
     * @param identifier when clearing group cache data, need to provide id of the group to clear.
     */
    private clearCache(formType: FormType, identifier?: string) {
        switch (formType) {
            case FormType.Groups:
                if (!identifier) {
                    break;
                }
                this.getFormsInGroupDataStreamCache[identifier] = null;
                break;
            case FormType.Users:
                this.getMyFormsDataStreamCache = null;
                break;
        }
    }

    private convertConsumerPuidToGuid(puid: string) {
        return '00000000-0000-0000-'.concat(puid.slice(0, 4), '-', puid.slice(4));
    }

    private getFormsDefaultImageName(name: string) {
        name = name || 'Untitled form';
        const defaultThemeVeinCount = this.formsConfigService.config.defaultImagesAvailable;
        const hashNumber = Math.abs(HashUtility.toHash(name)) % defaultThemeVeinCount + 1;
        return `default-${hashNumber}.png`;
    }

    // Todo: Cache Themes Data
    private getFormsThemingData(): Promise<FormsThemeMappingSet> {
        if (this.getFormsThemeMappingSetCache) {
            return this.getFormsThemeMappingSetCache;
        }
        this.getFormsThemeMappingSetCache = new Promise((resolve, reject) => {
            this.get<FormsThemeMappingSet>(KnownInstrumentationEvents.FormsThemeRequest, KnownRequestInstrumentationEvents.FormsThemeRequest, this.formsConfigService.config.formsApiPath + `${this.inMockMode ? FormsService.FORMS_THEMES_MOCK : FormsService.FORMS_THEMES}`, (promise: InstrumentedHttpPromise<FormsThemeMappingSet>) => {
                promise.instrument((result, params) => {
                    params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                }).then(response => {
                    resolve(response.data);
                });
            }, error => {
                this.getFormsThemeMappingSetCache = null;
                reject(error);
            }, this.getRequestConfig(),
                FetchStrategy.NetworkFirst,
                null,
                null,
                null,
                AccessTokenConsumer.None);
        });
        return this.getFormsThemeMappingSetCache;
    }

    private getUserReputationTier(): Promise<UserReputationTier> {
        return new Promise((resolve, reject) => {
            this.get<UserReputationTier>(
                KnownInstrumentationEvents.GetUserReputationTier,
                KnownRequestInstrumentationEvents.GetUserReputationTier,
                this.formsConfigService.config.formsApiPath + FormsService.USER_REPUTATION_TIER_PATH,
                (promise: InstrumentedHttpPromise<UserReputationTier>) => {
                    promise.instrument((result, params) => {
                        params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                    }).then(response => {
                        if (response && response.data) {
                            resolve(response.data);
                        } else {
                            reject();
                        }
                    });
                },
                error => {
                    reject(error);
                },
                this.getRequestConfig(),
                FetchStrategy.NetworkOnly);
        });
    }
}

function getUniqueNameFromList(name: string, list: Array<string>): string {
    if (list.indexOf(name) !== -1) {
        let baseName = name + ' ';
        let count = 1;
        const result = name && name.match(/([\s\S]+)\(([1-9]\d*)\)$/);

        if (result && result.length === 3) {
            baseName = result[1];
            count = parseInt(result[2], 10);
        }

        while (list.indexOf(name) !== -1) {
            name = `${baseName}(${++count})`;
        }
    }

    return name;
}
